import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../../store";
import {
  decrementRequests,
  incrementRequests,
  setIsLoading,
  setShowToast,
} from "./application";
import { apiUrl } from "api/apiUrl";
import { api } from "api/axiosInterceptor";
import {
  InvoiceTableData,
  PaymentMethodsData,
  PricingPlan,
  PricingResponse,
} from "types/subscription";
import { convertToCamelCase } from "utils/utils";

interface SubscriptionState {
  isProcessing: boolean;
  error: string | null;
  currentSubscription: any | null;
  invoiceTableData: InvoiceTableData;
  paymentMethodsData: PaymentMethodsData;
  pricingPlan: { data: PricingPlan[] };
}

const initialState: SubscriptionState = {
  isProcessing: false,
  error: null,
  currentSubscription: null,
  invoiceTableData: {
    data: [],
    pagination: {
      pageNumber: 0,
      pageLength: 0,
      total: 0,
      totalPages: 0,
    },
  },
  paymentMethodsData: {
    data: [],
  },
  pricingPlan: {
    data: [],
  },
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCurrentSubscription: (state, action: PayloadAction<any>) => {
      state.currentSubscription = action.payload;
    },
    setInvoiceTableData: (state, action: PayloadAction<InvoiceTableData>) => {
      state.invoiceTableData = action.payload;
    },
    setPaymentMethodsData: (
      state,
      action: PayloadAction<PaymentMethodsData>
    ) => {
      state.paymentMethodsData = {
        ...action.payload,
        data: action.payload.data,
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    setPricingPlan: (state, action: PayloadAction<PricingResponse>) => {
      state.pricingPlan = {
        ...state.pricingPlan,
        data: action.payload?.data,
      };
    },
    deletePaymentMethodReducer: (
      state,
      action: PayloadAction<string | number>
    ) => {
      state.paymentMethodsData.data = state.paymentMethodsData.data.filter(
        (method) => method.id !== action.payload
      );
    },
    setDefaultPaymentMethodReducer: (
      state,
      action: PayloadAction<string | number>
    ) => {
      state.paymentMethodsData.data = state.paymentMethodsData.data.map(
        (method) => ({
          ...method,
          isDefaultMethod: method.id === action.payload,
        })
      );
    },
  },
});

export const {
  setProcessing,
  setError,
  setCurrentSubscription,
  clearError,
  setInvoiceTableData,
  setPaymentMethodsData,
  setPricingPlan,
  deletePaymentMethodReducer,
  setDefaultPaymentMethodReducer,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;

export function createSubscription(payload: any) {
  return async (dispatch: AppDispatch, getState: () => any) => {
    const { subscriptionData } = getState();
    if (subscriptionData.isProcessing) {
      return { success: false, error: "Already processing" };
    }

    dispatch(setProcessing(true));
    dispatch(clearError());

    try {
      const response = await api.post(apiUrl.createSubscription, payload);

      if (response.status === 200 || response.status === 201) {
        dispatch(setCurrentSubscription(response.data));
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "Subscription created successfully!",
          })
        );
        return { success: true, data: response.data };
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to create subscription. Please try again.";

      dispatch(setError(errorMessage));
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: errorMessage,
        })
      );
      return { success: false, error: errorMessage };
    } finally {
      dispatch(setProcessing(false));
    }
  };
}

export function getSubscriptionDetails(planName: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());

    try {
      const response = await api.get(
        `/subscriptions/details/?plan_name=${planName}`
      );
      const formattedData = convertToCamelCase(response.data);

      if (response.status === 200) {
        dispatch(setPricingPlan(formattedData));
        return { success: true, data: formattedData };
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || "Failed to fetch subscription details.";

      dispatch(setError(errorMessage));
      return { success: false, error: errorMessage };
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function getInvoices(page: number, pageLength = 10) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));

    try {
      const response = await api.post(apiUrl.invoices, {
        page_number: page,
        page_length: pageLength,
      });

      if (response.status === 200) {
        const formattedData = convertToCamelCase(response.data);
        dispatch(setInvoiceTableData(formattedData));
        return response.status;
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || "Failed to fetch invoices.";

      dispatch(setError(errorMessage));
      return { success: false, error: errorMessage };
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function getPaymentMethods() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const response = await api.get(`${apiUrl.paymentMethods}details/`);
      const formattedData = convertToCamelCase(response.data);
      dispatch(setPaymentMethodsData(formattedData));
      return response.status;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || "Failed to fetch payment methods.";

      dispatch(setError(errorMessage));
      return { success: false, error: errorMessage };
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function buyCredit(payload: any) {
  return async (dispatch: AppDispatch, getState: () => any) => {
    const { subscriptionData } = getState();
    if (subscriptionData.isProcessing) {
      return { success: false, error: "Already processing" };
    }

    dispatch(setProcessing(true));
    dispatch(clearError());

    try {
      const response = await api.post(apiUrl.buyCredit, payload);

      if (response.status === 200 || response.status === 201) {
        dispatch(setCurrentSubscription(response?.data));
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "Subscription created successfully!",
          })
        );
        return { success: true, data: response?.data };
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to create subscription. Please try again.";

      dispatch(setError(errorMessage));
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: errorMessage,
        })
      );
      return { success: false, error: errorMessage };
    } finally {
      dispatch(setProcessing(false));
    }
  };
}

export function addPaymentMethod(payload: { token: string }) {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await api.post(`${apiUrl.paymentMethods}add/`, payload);
      dispatch(
        setShowToast({
          show: true,
          type: "success",
          toastMessage: "Payment method added successfully!",
        })
      );
      return response.status;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || "Failed to add payment method.";

      dispatch(setError(errorMessage));
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: errorMessage,
        })
      );
      return { success: false, error: errorMessage };
    }
  };
}

export function deletePaymentMethod(id: string | number) {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await api.post(`${apiUrl.paymentMethods}detach/`, {
        payment_method_id: id,
      });
      dispatch(
        setShowToast({
          show: true,
          type: "success",
          toastMessage: "Payment method deleted successfully!",
        })
      );
      if (response.status === 200) {
        dispatch(deletePaymentMethodReducer(id));
      }
      return response.status;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || "Failed to delete payment method.";

      dispatch(setError(errorMessage));
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: errorMessage,
        })
      );
      return { success: false, error: errorMessage };
    }
  };
}

export function setDefaultPaymentMethod(id: string | number) {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await api.post(`${apiUrl.paymentMethods}set-default/`, {
        payment_method_id: id,
      });
      dispatch(
        setShowToast({
          show: true,
          type: "success",
          toastMessage: "Payment method set as default successfully!",
        })
      );
      if (response.status === 200) {
        dispatch(setDefaultPaymentMethodReducer(id));
      }
      return response.status;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to set payment method as default.";

      dispatch(setError(errorMessage));
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: errorMessage,
        })
      );
      return { success: false, error: errorMessage };
    }
  };
}

export function getPlanDetails(plan?: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = apiUrl.planDetails;
      const response = await api.get(plan ? `${path}?plan_name=${plan}` : path);
      const formattedData = convertToCamelCase(response.data);

      if (response.status === 200) {
        dispatch(setPricingPlan(formattedData));
        return formattedData;
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || "Failed to fetch subscription details.";

      dispatch(setError(errorMessage));
      return { success: false, error: errorMessage };
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function cancelSubscription() {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await api.get(apiUrl.cancelSubscription);

      dispatch(
        setShowToast({
          show: true,
          type: "success",
          toastMessage: "Subscription cancelled successfully!",
        })
      );

      return response.status;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || "Failed to cancel subscription.";
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: errorMessage,
        })
      );

      return { success: false, error: errorMessage };
    }
  };
}